<template>
  <div ref="container" @scroll.passive="onScroll" :class="{'recycler': true, 'recycler--overflow-x-hide': hideScrollingX}">
    <ul ref="list" :style="ulStyle" class="recycler__list">
      <transition-group :name="name">
        <slot v-for="index in indices" :style="liStyle(index)" :index="index" :key="index % maxIndices"></slot>
      </transition-group>
    </ul>
  </div>
</template>

<script setup>
/**
 * This component aims to facilitate long lists of equally sized items, for example issue cards.
 * It does so by only rendering the elements that are visible in the scroll view at any scroll position.
 * It also lazy-loads any images in the cards. It also defers the loading of the items until scrolling stops, or after a short amount of time.
 * This can cause the scrolling to look a little janky so it's still up in the air if this was a worthwhile optimisation.
 */

import { computed, onMounted, onBeforeUnmount, ref, provide, watchEffect } from 'vue';

import { convertRemToPixels } from '@/functions/utils';

const props = defineProps({
  hideScrollingX: { type: Boolean, required: false, default: false },
  count: { type: Number, required: true },
  height: { type: Number, required: true },
  name: { type: String, default: 'list' },
  gap: { type: Number, default: 0 },
});

const container = ref(null);
const list = ref(null);
const params = ref(null);
const scrolling = ref(false);
const maxIndices = ref(10);

let scrollTimeout = null;
let scrollTicking = false;
let resizeTicking = false;

const ulStyle = computed(() => ({
  height: `${props.count * (props.height + props.gap)}rem`,
  position: 'relative',
}));

const indices = computed(() => {
  if (!params.value) return;

  let minIndex = Math.max(0, Math.floor(params.value.top / (convertRemToPixels(props.height + props.gap))));
  let maxIndex = Math.min(props.count - 1, Math.floor((params.value.bottom) / (convertRemToPixels(props.height + props.gap))));
  let newIndices = [];
  for (let i = minIndex; i <= maxIndex; i++) {
    newIndices.push(i);
  }
  return newIndices;
});

provide('scrolling', scrolling);

onMounted(() => {
  update();
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('scroll', onScroll, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
  window.removeEventListener('scroll', onScroll);
});

watchEffect(() => maxIndices.value = Math.max(maxIndices.value, indices.value?.length || 0));

function liStyle(index) {
  return {
    position: 'absolute',
    left: '0',
    width: '100%',
    height: `${props.height}rem`,
    top: `${index * (props.height + props.gap)}rem`,
  };
}

function setScrollTimeout() {
  scrollTimeout = setTimeout(() => {
    scrolling.value = false;
  }, 300);
}

function update() {
  if (!container.value) return;

  let offsetTop = container.value.offsetTop;
  let scrollY = window.scrollY;
  let scrollTop = container.value.scrollTop;
  let clientHeight = container.value.clientHeight;
  let innerHeight = window.innerHeight;

  let top = Math.max(scrollTop, scrollY - offsetTop);
  let height = Math.min(clientHeight, innerHeight - Math.max(0, offsetTop - scrollY));

  params.value = {
    top,
    bottom: top + height,
  };
}

function onScroll() {
  scrolling.value = true;
  if (scrollTimeout !== null) {
    clearTimeout(scrollTimeout);
  }
  setScrollTimeout();

  if (!scrollTicking) {
    setTimeout(() => {
      update();
      scrollTicking = false;
    }, 100);

    scrollTicking = true;
  }
}

function onResize() {
  if (!resizeTicking) {
    setTimeout(() => {
      update();
      resizeTicking = false;
    }, 100);

    resizeTicking = true;
  }
}

function scrollTo (index, { behavior = 'smooth', block = 'nearest', force = false } = {}) {
  let nearest = block === 'nearest';
  let { scrollTop, clientHeight } = container.value;
  let propsHeightPx = convertRemToPixels(props.height + props.gap);
  let scrollTarget = index * propsHeightPx;
  let top = scrollTop;
  if (scrollTarget < scrollTop || force) {
    top = nearest ? scrollTarget - 0.1*clientHeight : scrollTarget;
  } else if (scrollTarget + propsHeightPx > (scrollTop + clientHeight)) {
    top = nearest ? scrollTarget - clientHeight + propsHeightPx + 0.1*clientHeight : scrollTarget;
  }

  container.value.scrollTo({ top, behavior });
}
</script>
